'use client'

import { useMemo } from 'react'
import { useConvexAuth, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { parsePartnerSubdomain, parseCustomDomainHost } from '@/lib/subdomain'

/**
 * Branding for the partner that owns the current subdomain (e.g. acme.42a.ai).
 *
 * Returns:
 *   - `undefined` while the subdomain lookup is in flight
 *   - `null` when not on a partner subdomain, or the label doesn't resolve to
 *     an active white-labeled partner
 *   - `{ partnerName, logoUrl, logoDarkUrl }` otherwise
 */
export function useSubdomainPartnerBranding() {
  // Subdomain is stable for the page lifetime, read it once.
  const subdomain = useMemo(
    () => (typeof window !== 'undefined' ? parsePartnerSubdomain(window.location.hostname) : null),
    [],
  )
  const branding = useQuery(
    api.partners.getPartnerBrandingBySubdomain,
    subdomain ? { subdomain } : 'skip',
  )
  // Off-subdomain the query is skipped (always undefined); report that as a
  // resolved null rather than a perpetual loading state.
  return subdomain ? branding : null
}

/**
 * Branding for the partner that owns the current host, whichever whitelabel
 * surface that is: a partner subdomain (acme.42a.ai) or a verified
 * customer-owned custom domain (app.acme.com).
 *
 * Returns:
 *   - `undefined` while a host lookup is in flight
 *   - `null` when the host isn't a whitelabel surface, or doesn't resolve to
 *     an active white-labeled partner
 *   - `{ partnerName, logoUrl, logoDarkUrl, defaultLocale }` otherwise
 */
export function useHostPartnerBranding() {
  const subdomainBranding = useSubdomainPartnerBranding()

  // Custom-domain host is only relevant when not on a known partner
  // subdomain — mirrors the middleware's mutually-exclusive routing.
  const customDomainHost = useMemo(
    () =>
      typeof window !== 'undefined' && !parsePartnerSubdomain(window.location.hostname)
        ? parseCustomDomainHost(window.location.host)
        : null,
    [],
  )
  const customDomainBranding = useQuery(
    api.partners.getPartnerBrandingByCustomDomain,
    customDomainHost ? { host: customDomainHost } : 'skip',
  )

  if (subdomainBranding === undefined) return undefined
  if (customDomainHost) return customDomainBranding
  return subdomainBranding
}

/**
 * Resolve the partner branding to apply to the app chrome (sidebar logo,
 * "powered by 42A" chip).
 *
 * Precedence:
 *   1. The partner that owns the current host is highest — a subdomain
 *      (e.g. acme.42a.ai) or a verified customer-owned custom domain (e.g.
 *      app.acme.com). On a partner's whitelabel domain the visitor always
 *      sees that partner's branding, regardless of which referral code the
 *      signed-in org is affiliated with.
 *   2. The signed-in user's org partner (via referral code). Applies on
 *      non-partner hosts (app.42a.ai) and when the host doesn't resolve
 *      to an active white-labeled partner.
 *   3. Otherwise null (default 42A chrome).
 *
 * Returns:
 *   - `undefined` while loading (keep 42A defaults)
 *   - `null` when neither source applies
 *   - `{ partnerName, logoUrl, logoDarkUrl, ... }` otherwise (org result also
 *     carries `partnerId`). `logoUrl`/`logoDarkUrl` are null when the partner
 *     hasn't uploaded that variant.
 */
export function usePartnerBranding() {
  // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
  const { isAuthenticated } = useConvexAuth()

  const orgBranding = useQuery(
    api.partners.getCurrentOrgPartnerBranding,
    isAuthenticated ? {} : 'skip',
  )

  const hostBranding = useHostPartnerBranding()

  // While the host lookup is in flight, stay in the loading state so the
  // org partner's logo never flashes on someone else's whitelabel domain.
  if (hostBranding === undefined) return undefined

  // Host branding wins; fall through to org branding only off-whitelabel
  // hosts or when the host doesn't resolve to an active partner.
  return hostBranding ?? orgBranding
}
